import { Data, Deferred, Effect, Exit, Layer, Option, Schema } from 'effect';

import {
  BALIKOVNA_IFRAME_URL,
  BalikovnaMessageSchema,
  BalikovnaSelectionSchema,
  BalikovnaWidget,
  BalikovnaWidgetNotLoadedError,
  BalikovnaWidgetPayloadError,
  toBalikovnaT,
} from './BalikovnaWidget';
import type { BalikovnaT, BalikovnaWidgetError, BalikovnaWidgetOptions } from './BalikovnaWidget';

const IFRAME_MARKER = 'data-balikovna-widget';

const decodeMessage = Schema.decodeUnknownOption(BalikovnaMessageSchema);
const decodeSelection = Schema.decodeUnknownExit(BalikovnaSelectionSchema);

/**
 * One frame from the page's `message` stream, classified.
 *
 * `Ignore` covers everything that is not the carrier answering: another
 * script's message, an unrelated object, a string.
 */
/* eslint-disable @typescript-eslint/no-empty-object-type */
type Frame = Data.TaggedEnum<{
  Ignore: {};
  Selected: { readonly point: BalikovnaT };
  Malformed: { readonly cause: unknown };
}>;
/* eslint-enable @typescript-eslint/no-empty-object-type */

const Frame = Data.taggedEnum<Frame>();

const classify = (data: unknown): Frame =>
  Option.match(decodeMessage(data), {
    onNone: () => Frame.Ignore(),
    onSome: () =>
      Exit.match(decodeSelection(data), {
        onFailure: (cause) => Frame.Malformed({ cause }),
        onSuccess: (selection) => Frame.Selected({ point: toBalikovnaT(selection) }),
      }),
  });

/**
 * Opens the picker and waits for the shopper's choice.
 *
 * The service owns the iframe: the acquire appends one into the caller's
 * embed together with the `message` listener, and the release removes both on
 * every exit — a resolved pick, a failed one, and an interrupted one alike.
 * Without that, reopening the picker would leave a stale iframe and a stale
 * listener behind.
 *
 * The iframe carries no `title`. Its accessible name comes from the dialog
 * that hosts it (`PickupPointPickerPanel`'s `aria-label`), which is copy the
 * tenant owns.
 */
const openWidget = (
  options: BalikovnaWidgetOptions
): Effect.Effect<BalikovnaT, BalikovnaWidgetError> =>
  Effect.scoped(
    Effect.gen(function* () {
      const embed = document.querySelector(options.embedSelector);

      if (embed === null) {
        return yield* new BalikovnaWidgetNotLoadedError({
          message: `The Balíkovna embed element "${options.embedSelector}" is not in the document`,
        });
      }

      const selection = yield* Deferred.make<BalikovnaT, BalikovnaWidgetPayloadError>();

      const handleMessage = (event: MessageEvent) => {
        Frame.$match(classify(event.data), {
          Ignore: () => undefined,
          Malformed: (frame) => {
            Deferred.doneUnsafe(
              selection,
              Effect.fail(
                new BalikovnaWidgetPayloadError({
                  message: 'The Balíkovna widget sent a point this client cannot read',
                  cause: frame.cause,
                })
              )
            );
          },
          Selected: (frame) => {
            Deferred.doneUnsafe(selection, Effect.succeed(frame.point));
          },
        });
      };

      yield* Effect.acquireRelease(
        Effect.sync(() => {
          const iframe = document.createElement('iframe');
          iframe.setAttribute(IFRAME_MARKER, '');
          iframe.src = BALIKOVNA_IFRAME_URL;
          iframe.allow = 'geolocation';
          embed.appendChild(iframe);
          window.addEventListener('message', handleMessage);
          return iframe;
        }),
        (iframe) =>
          Effect.sync(() => {
            window.removeEventListener('message', handleMessage);
            iframe.remove();
          })
      );

      return yield* Deferred.await(selection);
    })
  );

// There is no carrier API to close: the picked iframe removes itself via
// `pick`'s own finalizer, and there is nothing else the service holds open.
const closeWidget = Effect.void;

const make = Effect.succeed({
  pick: (options: BalikovnaWidgetOptions) => openWidget(options),
  close: closeWidget,
});

export const BalikovnaWidgetLive = Layer.effect(BalikovnaWidget, make);
